"use client";
import React, { useEffect, useState } from "react";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import Table from "../../../../components/Table";
import NameSearchFilter from "../../../../components/name-search-filter";
import Pagination from "../../../../components/Pagination";
import Button from "../../../../components/Button";
import Checkbox from "../../../../components/Checkbox"
import styles from "../../../global/css/customer-details.module.css";
import { useParams, useLocation } from "react-router-dom";
import { fetchPatientMedicationHistory } from "../../../../api/dispensary/script/processScriptApi.js";

export default function MedicationHistory() {
  const { patientId, patient_id } = useParams();
  const location = useLocation();
  const patientName = location?.state?.patientName || "";
  const pid = patientId || patient_id;
  const [allRows, setAllRows] = useState([]);
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [checkboxChecked, setCheckboxChecked] = useState(false); 
  const itemsPerPage = 5;

  const columns = [
    {
      header: "Script No",
      render: (_, row) => {
        const t = String(row.type || '').toLowerCase();
        // Always prefer an OTC reference if one exists, even if the type on
        // older rows is still marked as "process".
        if (row.otcReference) return String(row.otcReference);
        if (t === 'process') return String(row.scriptNumber ?? row.processscriptnumber ?? row.processScriptNumber ?? row.scriptNo ?? row.scriptno ?? '');
        if (t === 'clinic') return String(row.clinicReference ?? row.clinicreference ?? row.scriptNo ?? row.scriptno ?? '');
        // For unknown types, fall back to internal id
        return String(row.scriptNo ?? row.scriptno ?? '');
      }
    },
    { header: "Date", accessor: "date" },
    { header: "Medication Name", accessor: "medication" },
    { header: "ICD-10", accessor: "icd10" },
    { header: "Dosage", accessor: "dosage" },
    { header: "Dosage Form", accessor: "dosageForm" },
    { header: "Prescription Type", accessor: "prescriptionType" },
  ];

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);
    const filtered = allRows.filter((row) =>
      (row.medication || "").toLowerCase().includes(term)
    );
    setData(filtered);
    setCurrentPage(1);
  };

  useEffect(() => {
    let ignore = false;
    async function load() {
      if (!pid) return;
      const rows = await fetchPatientMedicationHistory(pid);
      if (ignore) return;
      setAllRows(rows || []);
      const filtered = (rows || []).filter((row) =>
        (row.medication || "").toLowerCase().includes(searchTerm)
      );
      setData(filtered);
      setCurrentPage(1);
    }
    load();
    return () => { ignore = true; };
  }, [pid]);

  const handlePageChange = (page) => setCurrentPage(page);

  const filteredData = data.filter(r => String(r.type || '').toLowerCase() !== 'otc');
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className={styles.container}>
      <Navbar />
      <main className={styles.main}>
        <h1 className={styles.title}>Medication History</h1>

        <div className={styles.filterWrapper}>
          <NameSearchFilter onChange={handleSearch} placeholder="Search by medication..." />
        </div>

        <Table columns={columns} data={paginatedData} />
        <div className={styles.downloadBar}>
          <Button
            onClick={async () => {
              try {
                const [{ jsPDF }, autoTable] = await Promise.all([
                  import("jspdf"),
                  import("jspdf-autotable")
                ]);
                const doc = new jsPDF({ orientation: "p", unit: "pt", format: "a4" });
                const now = new Date();
                const dateStr = now.toLocaleDateString();
                // Header text: patient name (preferred) or fallback title, then date
                doc.setFontSize(14);
                doc.text(patientName || "Medication History", 40, 40);
                doc.setFontSize(11);
                const metaLine = `Date: ${dateStr}`;
                doc.text(metaLine, 40, 58);
                const headers = columns.map(c => c.header);
                const rows = (filteredData || []).map(row =>
                  columns.map(col =>
                    col.render ? col.render(null, row) : (row[col.accessor] ?? "")
                  )
                );
                autoTable.default(doc, {
                  head: [headers],
                  body: rows,
                  theme: "grid",
                  styles: {
                    fontSize: 9,
                    fillColor: [255, 255, 255], // white rows
                    lineColor: [228, 214, 246], // lavender borders
                    lineWidth: 0.5,
                  },
                  headStyles: {
                    fillColor: [228, 214, 246], // lavender header bg
                    textColor: [51, 65, 85],    // slate text
                  },
                  alternateRowStyles: {},
                  margin: { top: 78 },
                });
                doc.save("medication_history.pdf");
              } catch (e) {
                alert("PDF generation is unavailable. Please ensure jspdf and jspdf-autotable are installed.");
              }
            }}
            variant="primary"
          >
            Download PDF
          </Button>
        </div>
        <div className={styles.paginationWrapper}>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>


      </main>
      <Footer />
    </div>
  );
}
